import { Core } from "@faire/design-tokens";
import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import { strictLocalize } from "@faire/web/common/localization/strictLocalize";
import { Tooltip } from "@faire/web/slate/Tooltip";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import classNames from "classnames";
import React from "react";

import * as styles from "./promotedBadge.css";

interface IProps {
  noPadding?: boolean;
  promotedBy: Array<keyof typeof IProduct.PromotedType> | undefined;
}

export const PromotedBadge: React.FC<IProps> = ({
  noPadding,
  promotedBy = [],
}) => {
  const tooltipContents = isPromotedByAds(promotedBy)
    ? [
        {
          title: undefined,
          body: strictLocalize({
            defaultMessage:
              "You’re seeing this promoted product based on its relevance to your search query or browsing history on Faire.",
            description: {
              text: "This is the content of a tooltip to explain why this product is promoted by Faire",
            },
          }),
        },
      ]
    : [
        {
          title: strictLocalize({
            defaultMessage: "Promoted by Faire",
            description: {
              text: "This is a title of a tooltip to explain why this product is promoted by Faire",
            },
          }),
          body: strictLocalize({
            defaultMessage:
              "This brand has earned increased exposure on Faire by helping us grow Faire's retailer community.",
            description: {
              text: "This is the content of a tooltip to explain why this product is promoted by Faire",
            },
          }),
        },
      ];
  return (
    <Tooltip
      position="bottom"
      icon={
        <Typography
          variant="labelSansRegular"
          color={Core.text.subdued}
          className={noPadding ? classNames(styles.badgeNoPadding) : ""}
        >
          <StrictLocalMsg
            defaultMessage="Promoted"
            description={{
              text: "This is a text below a product tile to show that this product is promoted by Faire.",
            }}
          />
        </Typography>
      }
      contents={tooltipContents}
    />
  );
};

const isPromotedByAds = (
  promotedBy: Array<keyof typeof IProduct.PromotedType>
): boolean => {
  const promotedByAdsIndex = promotedBy.findIndex(
    (v) => v === IProduct.PromotedType.ADS
  );
  const promotedByFaireDirectIndex = promotedBy.findIndex(
    (v) => v === IProduct.PromotedType.FAIRE_DIRECT_REWARDS
  );

  if (promotedByAdsIndex < 0) {
    return false;
  }

  return promotedByAdsIndex > promotedByFaireDirectIndex;
};
